export interface Market {
  /** Canonical ISO 3166-1 alpha-2 country code. The internal source of truth. */
  canonical: string;
  /** Country-code aliases accepted from external systems (LearnUpon, marketing tokens, etc.). */
  aliases: readonly string[];
  /** URL path segment (e.g. /no/, /sv/). May be shared across markets that use the same language. */
  urlPrefix: string;
  /** Translation-file key under frontend/messages — must correspond to a `${language}.json` file. */
  language: string;
  /** Phone dial code, e.g. '+47'. */
  dialCode: string;
  /** Human-readable display name. */
  name: string;
}

export const markets = [
  { canonical: 'DK', aliases: ['DK'],             urlPrefix: 'da', language: 'da', dialCode: '+45',  name: 'Denmark' },
  { canonical: 'NL', aliases: ['NL'],             urlPrefix: 'nl', language: 'nl', dialCode: '+31',  name: 'Netherlands' },
  { canonical: 'GB', aliases: ['GB'],             urlPrefix: 'en', language: 'en', dialCode: '+44',  name: 'United Kingdom' },
  { canonical: 'IE', aliases: ['IE'],             urlPrefix: 'en', language: 'en', dialCode: '+353', name: 'Ireland' },
  { canonical: 'FR', aliases: ['FR'],             urlPrefix: 'fr', language: 'fr', dialCode: '+33',  name: 'France' },
  { canonical: 'NO', aliases: ['NO', 'NB', 'NN'], urlPrefix: 'no', language: 'no', dialCode: '+47',  name: 'Norway' },
  { canonical: 'SE', aliases: ['SE', 'SV'],       urlPrefix: 'sv', language: 'sv', dialCode: '+46',  name: 'Sweden' },
] as const satisfies readonly Market[];

const byAlias = new Map<string, Market>();
const byCanonical = new Map<string, Market>();
for (const m of markets) {
  for (const a of m.aliases) byAlias.set(a.toUpperCase(), m);
  byCanonical.set(m.canonical.toUpperCase(), m);
}

/** Resolve any country-code input (canonical or alias) to its market. */
export function resolveMarket(input: string | null | undefined): Market | undefined {
  if (!input) return undefined;
  return byAlias.get(input.trim().toUpperCase());
}

/** Look up the market by canonical country code only — does not accept aliases. */
export function getMarketByCanonical(canonical: string): Market | undefined {
  return byCanonical.get(canonical.toUpperCase());
}

/**
 * Resolve a URL prefix to its translation-file language.
 * When markets share a URL prefix (e.g. GB and IE both use /en/), they must
 * also share a language, so the lookup is unambiguous.
 */
export function getLanguageForUrlPrefix(prefix: string): string | undefined {
  return markets.find((m) => m.urlPrefix === prefix.toLowerCase())?.language;
}

/**
 * Resolve any URL path segment — canonical prefix (`no`) or country alias (`nb`,
 * `nn`, `se`, `dk`, `gb`) — to the canonical URL prefix for that market.
 * Returns the input as-is when it's already canonical, and `undefined` for
 * unknown segments (which the caller should treat as "not a market prefix").
 */
export function resolveUrlPrefix(segment: string): string | undefined {
  const lower = segment.toLowerCase();
  if (markets.some((m) => m.urlPrefix === lower)) return lower;
  return byAlias.get(segment.toUpperCase())?.urlPrefix;
}

/** Distinct URL prefixes — feeds next-intl routing and the middleware locale regex. */
export const urlPrefixes = Array.from(
  new Set(markets.map((m) => m.urlPrefix))
) as ReadonlyArray<(typeof markets)[number]['urlPrefix']>;
